import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ArrowRight, CheckCircle2, Zap, Users, Calendar, Layers, Shield,
  Menu, X, Facebook, Instagram, Youtube, Linkedin, Video, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { ScrollCtaBanner } from '../components/ScrollCtaBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Static fallback used only when the API call fails (network blip, cold
// backend) — keeps the landing page from rendering an empty pricing section.
const FALLBACK_TIERS = [
  { id: 'starter', name: 'Starter', price: 99, features: ['AI Post Generation (Text + Image)', 'Social Scheduling', 'Up to 300 Images / Month'] },
  { id: 'growth', name: 'Growth', price: 159, features: ['Everything in Starter', 'Up to 800 Images / Month', 'AI Auto-Scheduler'], is_popular: true },
  { id: 'pro', name: 'Pro', price: 199, features: ['Everything in Growth', 'Up to 800 Images / Month', 'AI Post Manager (Fully Automated Posting)'] },
];

export const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(true);

  // Pull live plans from the same /subscription/tiers endpoint the /pricing
  // page and Stripe checkout use, so disabling a plan from Admin → Pricing
  // instantly hides it from the marketing landing page too. Falls back to a
  // hardcoded list if the API is unreachable so the page never renders empty.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/subscription/tiers`);
        if (cancelled) return;
        // Marketing teaser intentionally excludes Free (lead magnet lives in
        // the nav "Sign up free" CTA) and trial/archived plans — but does
        // NOT cap by count, so adding a 4th paid plan in Admin → Pricing
        // automatically shows up here.
        const paid = (r.data?.tiers || [])
          .filter((t) => !t.is_trial && (t.price ?? 0) > 0 && !t.archived)
          .sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
        setTiers(paid.length ? paid : FALLBACK_TIERS);
      } catch (_) {
        if (!cancelled) setTiers(FALLBACK_TIERS);
      } finally {
        if (!cancelled) setTiersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans text-zinc-950 selection:bg-indigo-100 selection:text-indigo-900">
      {/* Sticky Nav */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl tracking-tight">PostToSocial</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-zinc-600 hover:text-zinc-950 transition-colors" data-testid="nav-features">Features</a>
              <a href="#how-it-works" className="text-sm font-medium text-zinc-600 hover:text-zinc-950 transition-colors" data-testid="nav-how-it-works">How It Works</a>
              <a href="/pricing" className="text-sm font-medium text-zinc-600 hover:text-zinc-950 transition-colors" data-testid="nav-pricing">Pricing</a>
              <div className="flex items-center gap-4 border-l border-zinc-200 pl-8">
                <a href="/login" className="text-sm font-medium text-zinc-600 hover:text-zinc-950 transition-colors" data-testid="nav-signin">Sign in</a>
                <Button asChild className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="nav-get-started">
                  <a href="/pricing">Get Started</a>
                </Button>
              </div>
            </div>

            <div className="md:hidden">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-zinc-600" data-testid="nav-mobile-toggle">
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile menu panel */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-zinc-200 py-4 space-y-1" data-testid="nav-mobile-menu">
              <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block px-2 py-2.5 text-base font-medium text-zinc-700 hover:bg-zinc-50 rounded-md" data-testid="nav-mobile-features">Features</a>
              <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="block px-2 py-2.5 text-base font-medium text-zinc-700 hover:bg-zinc-50 rounded-md" data-testid="nav-mobile-how-it-works">How It Works</a>
              <a href="/pricing" className="block px-2 py-2.5 text-base font-medium text-zinc-700 hover:bg-zinc-50 rounded-md" data-testid="nav-mobile-pricing">Pricing</a>
              <a href="/login" className="block px-2 py-2.5 text-base font-semibold text-indigo-600 hover:bg-indigo-50 rounded-md" data-testid="nav-mobile-signin">Sign in</a>
              <Button asChild className="w-full bg-zinc-950 text-white hover:bg-zinc-800 mt-2" data-testid="nav-mobile-get-started">
                <a href="/pricing">Get Started</a>
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="outline" className="mb-6 px-5 py-2 rounded-full border-indigo-200 bg-indigo-50 text-indigo-700 font-semibold tracking-tight text-base md:text-lg">
            Never Miss A Marketing Day.
          </Badge>
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter text-zinc-950 mb-6 max-w-4xl mx-auto leading-[1.1]">
            Stop buying marketing tools. <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-indigo-500">
              Start building your marketing department.
            </span>
          </h1>
          <p className="text-lg md:text-xl text-zinc-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            One platform to create content, write blogs, run email campaigns, generate AI images,
            schedule everything, and manage multiple brands — so you can grow your business consistently.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 mb-10 max-w-xl mx-auto text-left" data-testid="hero-feature-list">
            {[
              'Generate social media content',
              'Write blogs',
              'Create email campaigns',
              'Generate AI images',
              'Schedule content',
              'Manage multiple brands',
              'Grow your business consistently',
            ].map((feature) => (
              <li key={feature} className="flex items-center gap-3 text-base text-zinc-700">
                <CheckCircle2 className="w-5 h-5 text-indigo-600 shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white text-base h-12 px-8" data-testid="hero-primary-cta">
              <a href="/pricing">Start building — from $29/mo <ArrowRight className="ml-2 w-4 h-4" /></a>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto text-base h-12 px-8 border-zinc-200 hover:bg-zinc-50" data-testid="hero-secondary-cta">
              <a href="/pricing">See pricing</a>
            </Button>
          </div>
          <p className="text-base text-zinc-700 font-semibold mb-1" data-testid="hero-price-line">
            Starting at only $29/month.
          </p>
          <p className="text-sm text-zinc-500 font-medium mb-16">
            No contracts — cancel anytime.
          </p>

          {/* Browser Mockup */}
          <div className="relative mx-auto max-w-5xl">
            <div className="rounded-xl border border-zinc-200 bg-white/50 p-2 shadow-2xl backdrop-blur-sm">
              <div className="rounded-lg border border-zinc-200 bg-zinc-950 overflow-hidden">
                {/* Mac OS Chrome */}
                <div className="flex items-center px-4 py-3 border-b border-zinc-800 bg-zinc-950">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-zinc-700" />
                    <div className="w-3 h-3 rounded-full bg-zinc-700" />
                    <div className="w-3 h-3 rounded-full bg-zinc-700" />
                  </div>
                </div>
                <img 
                  src="/hero-posts-screenshot.png" 
                  alt="PostToSocial Dashboard showing My Posts and multi-platform scheduling" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="border-y border-zinc-200 bg-zinc-50 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-bold tracking-widest uppercase text-zinc-400 mb-8">
            Publish to 5 networks today. One workspace.
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 grayscale">
            <Linkedin className="w-8 h-8" />
            <Facebook className="w-8 h-8" />
            <Instagram className="w-8 h-8" />
            <Youtube className="w-8 h-8" />
            <Video className="w-8 h-8" />
          </div>
        </div>
      </section>

      {/* Capability Grid */}
      <section id="features" className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 max-w-3xl">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-950 mb-6">
              Everything a marketing department does. Minus the headcount.
            </h2>
            <p className="text-lg text-zinc-600">
              Content, blogs, email campaigns, AI images, scheduling, and multi-brand management — all in one platform, from prompt to published. Stop stitching together five subscriptions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="rounded-none sm:rounded-xl border-zinc-200 shadow-none hover:shadow-md transition-shadow">
              <CardContent className="p-8">
                <Zap className="w-8 h-8 text-indigo-600 mb-6" />
                <h3 className="text-xl font-bold tracking-tight mb-2">AI Post Generation</h3>
                <p className="text-zinc-600 leading-relaxed">Text plus AI image generation powered by gpt-image-1 and Gemini Nano Banana.</p>
              </CardContent>
            </Card>
            <Card className="rounded-none sm:rounded-xl border-zinc-200 shadow-none hover:shadow-md transition-shadow">
              <CardContent className="p-8">
                <CheckCircle2 className="w-8 h-8 text-indigo-600 mb-6" />
                <h3 className="text-xl font-bold tracking-tight mb-2">Live OAuth Publishing</h3>
                <p className="text-zinc-600 leading-relaxed">Not just drafts. We officially publish to LinkedIn, Facebook, Instagram, X, and YouTube via APIs. TikTok — coming soon.</p>
              </CardContent>
            </Card>
            <Card className="rounded-none sm:rounded-xl border-zinc-200 shadow-none hover:shadow-md transition-shadow md:row-span-2 bg-zinc-50 border-transparent relative overflow-hidden group">
              <CardContent className="p-8 h-full flex flex-col">
                <Calendar className="w-8 h-8 text-indigo-600 mb-6" />
                <h3 className="text-2xl font-bold tracking-tight mb-2">AI Post Manager</h3>
                <p className="text-zinc-600 leading-relaxed mb-8">Draft a 30-day, multi-platform campaign plan in ~17 seconds with chunked LLM batches running in parallel. Generates 120+ scheduled drafts instantly.</p>
                <div className="mt-auto border border-zinc-200 rounded-lg overflow-hidden shadow-sm group-hover:-translate-y-2 transition-transform duration-300">
                  <img src="/hero-aimgr-screenshot.png" alt="AI Campaign Manager UI" className="w-full object-cover" />
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-none sm:rounded-xl border-zinc-200 shadow-none hover:shadow-md transition-shadow">
              <CardContent className="p-8">
                <Layers className="w-8 h-8 text-indigo-600 mb-6" />
                <h3 className="text-xl font-bold tracking-tight mb-2">Full Content Suite</h3>
                <p className="text-zinc-600 leading-relaxed">Beyond social: generate blogs, email campaigns, carousels, video scripts, and ads — then manage multiple brands, all from one place.</p>
              </CardContent>
            </Card>
            <Card className="rounded-none sm:rounded-xl border-zinc-200 shadow-none hover:shadow-md transition-shadow">
              <CardContent className="p-8">
                <Shield className="w-8 h-8 text-indigo-600 mb-6" />
                <h3 className="text-xl font-bold tracking-tight mb-2">Enterprise Security</h3>
                <p className="text-zinc-600 leading-relaxed">Email verification, TOTP 2FA, Cloudflare Turnstile, and JWT auth to protect your client credentials.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Agency Section */}
      <section className="py-24 sm:py-32 bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge variant="outline" className="mb-6 px-4 py-1.5 rounded-full border-zinc-800 bg-zinc-900 text-zinc-300">
                Phase A Agency Tools
              </Badge>
              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter mb-6">
                Manage 30+ client accounts in one dashboard.
              </h2>
              <p className="text-xl text-zinc-400 mb-8 leading-relaxed">
                Segment connected destinations into named "Clients" with brand colors. Filter posts, calendars, and archives by client. Built for agencies who need strict separation of brand assets.
              </p>
              <ul className="space-y-4">
                {['Color-coded client tagging', 'Scoped master calendar views', 'Multi-tenant team workspaces', 'Bulk archive & CSV exports'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                    <span className="text-zinc-300 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-[2rem] opacity-20 blur-2xl"></div>
              <div className="relative rounded-2xl border border-zinc-800 bg-zinc-900 p-8 shadow-2xl">
                <div className="space-y-4">
                  {[
                    { name: 'Acme Corp', color: 'bg-blue-500', posts: 42 },
                    { name: 'Globex Inc', color: 'bg-emerald-500', posts: 28 },
                    { name: 'Stark Ind.', color: 'bg-red-500', posts: 115 }
                  ].map((client, i) => (
                    <div key={i} className="flex items-center justify-between p-4 rounded-lg bg-zinc-950 border border-zinc-800">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${client.color}`}></div>
                        <span className="font-semibold">{client.name}</span>
                      </div>
                      <Badge variant="secondary" className="bg-zinc-800 text-zinc-300 hover:bg-zinc-700">{client.posts} Scheduled</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 sm:py-32 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-950 mb-16 text-center">
            Manual posting vs PostToSocial
          </h2>
          <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden shadow-sm">
            <div className="grid grid-cols-3 bg-zinc-50 border-b border-zinc-200 p-6 font-bold text-sm uppercase tracking-widest text-zinc-500">
              <div className="col-span-1">Capability</div>
              <div className="col-span-1 text-center">The Old Way</div>
              <div className="col-span-1 text-center text-indigo-600">PostToSocial</div>
            </div>
            {[
              ['Time per post', '45 min', '30 sec'],
              ['Daily consistency', 'Skipped 3x/week', 'Auto-published 24/7'],
              ['Multi-platform', 'Copy-paste 6 times', 'One workspace click'],
              ['Multi-page management', 'Spreadsheet hell', 'Native multi-account'],
              ['Campaign planning', 'Hours of brainstorming', '120 posts in 17s'],
              ['Approval workflows', 'Email back-and-forth', 'Built-in status queues']
            ].map((row, i) => (
              <div key={i} className="grid grid-cols-3 p-6 border-b border-zinc-100 last:border-0 hover:bg-zinc-50 transition-colors items-center">
                <div className="col-span-1 font-semibold text-zinc-950">{row[0]}</div>
                <div className="col-span-1 text-center text-zinc-500">{row[1]}</div>
                <div className="col-span-1 text-center font-bold text-emerald-600 flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> {row[2]}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-950 mb-16 text-center">
            How it works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { num: '01', title: 'Connect Accounts', desc: 'Securely link your social profiles via official OAuth integrations.' },
              { num: '02', title: 'Describe Campaign', desc: 'Give the AI Post Manager a prompt, tone, and duration.' },
              { num: '03', title: 'AI Handles the Rest', desc: 'Review 120+ drafts. Click approve. APScheduler publishes exactly on time.' }
            ].map((step, i) => (
              <div key={i} className="relative pl-8 border-l-2 border-zinc-200 hover:border-indigo-600 transition-colors duration-300">
                <div className="text-5xl font-black text-zinc-100 absolute -top-6 left-6 -z-10">{step.num}</div>
                <h3 className="text-2xl font-bold tracking-tight mb-4 mt-4">{step.title}</h3>
                <p className="text-zinc-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className="py-24 sm:py-32 bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-950 mb-4">
              Simple, transparent pricing.
            </h2>
            <p className="text-lg text-zinc-600">Choose the plan that fits your growth.</p>
          </div>
          
          <div
            className={
              tiers.length === 1
                ? 'grid grid-cols-1 max-w-md mx-auto'
                : tiers.length === 2
                  ? 'grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto'
                  : tiers.length === 3
                    ? 'grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto'
                    : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto'
            }
            data-testid="landing-pricing-grid"
          >
            {tiersLoading ? (
              [0, 1, 2].map((i) => (
                <Card key={i} className="rounded-none sm:rounded-xl shadow-sm border-zinc-200 bg-white flex flex-col">
                  <CardContent className="p-8 flex-grow animate-pulse">
                    <div className="h-6 w-24 bg-zinc-200 rounded mb-3" />
                    <div className="h-10 w-32 bg-zinc-200 rounded mb-6" />
                    <div className="space-y-3 mb-8">
                      <div className="h-4 w-3/4 bg-zinc-100 rounded" />
                      <div className="h-4 w-2/3 bg-zinc-100 rounded" />
                      <div className="h-4 w-3/5 bg-zinc-100 rounded" />
                    </div>
                    <div className="h-10 w-full bg-zinc-100 rounded" />
                  </CardContent>
                </Card>
              ))
            ) : (
              tiers.map((tier, idx) => {
                // Highlight whichever plan the admin marked is_popular; if none
                // marked, default to the middle card (visually balanced).
                const popularExists = tiers.some((t) => t.is_popular);
                const isHighlight = popularExists ? tier.is_popular : idx === Math.floor(tiers.length / 2);
                const bullets = (tier.features || []).slice(0, 3);
                return (
                  <Card
                    key={tier.id}
                    data-testid={`landing-pricing-card-${tier.id}`}
                    className={
                      isHighlight
                        ? 'rounded-none sm:rounded-xl shadow-xl border-indigo-600 bg-white flex flex-col relative transform md:-translate-y-4'
                        : 'rounded-none sm:rounded-xl shadow-sm border-zinc-200 bg-white flex flex-col'
                    }
                  >
                    {isHighlight && (
                      <>
                        <div className="absolute top-0 inset-x-0 h-1 bg-indigo-600 rounded-t-xl" />
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white hover:bg-indigo-700">
                          Most Popular
                        </Badge>
                      </>
                    )}
                    <CardContent className="p-8 flex-grow flex flex-col">
                      <h3 className="text-xl font-bold mb-2">{tier.name}</h3>
                      <div className="mb-6">
                        {tier.discount_active && tier.discounted_price != null ? (
                          <div>
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="text-zinc-400 text-lg line-through">
                                ${Number(tier.price).toFixed(0)}
                              </span>
                              <span className="text-4xl font-extrabold tracking-tighter">
                                ${Number(tier.discounted_price).toFixed(2)}
                              </span>
                              <span className="text-zinc-500">/mo</span>
                            </div>
                            <p className="text-xs text-emerald-700 font-semibold mt-1">
                              Save {Math.round(tier.discount_percent)}%
                              {tier.discount_label ? ` — ${tier.discount_label}` : ' — limited time'}
                            </p>
                          </div>
                        ) : (
                          <>
                            <span className="text-4xl font-extrabold tracking-tighter">
                              ${Number(tier.price).toFixed(0)}
                            </span>
                            <span className="text-zinc-500">/mo</span>
                          </>
                        )}
                      </div>
                      <ul className="space-y-4 mb-8">
                        {bullets.map((feat, i) => (
                          <li
                            key={i}
                            className={`flex items-start gap-3 ${
                              isHighlight ? 'text-zinc-950 font-medium' : 'text-zinc-600'
                            }`}
                          >
                            <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                            <span>{feat}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        variant={isHighlight ? 'default' : 'outline'}
                        className={`w-full mt-auto ${isHighlight ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                        asChild
                        data-testid={`pricing-${tier.id}-cta`}
                      >
                        <a href="/pricing">View details</a>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-950 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-lg font-bold text-left">Does it actually publish or just generate drafts?</AccordionTrigger>
              <AccordionContent className="text-zinc-600 leading-relaxed text-base">
                We use official OAuth integrations to actually publish your posts directly to the platforms. Unlike tools that just give you text to copy-paste, our APScheduler worker ensures your content goes live exactly when scheduled.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-lg font-bold text-left">Which platforms are supported?</AccordionTrigger>
              <AccordionContent className="text-zinc-600 leading-relaxed text-base">
                Currently, we support full automated publishing to LinkedIn (personal & company), Facebook (personal & business), Instagram, Bluesky, and YouTube. TikTok is coming soon — awaiting platform approval.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-lg font-bold text-left">Can I manage multiple clients?</AccordionTrigger>
              <AccordionContent className="text-zinc-600 leading-relaxed text-base">
                Yes. Our platform includes Phase A agency tools, allowing you to segment connected destinations into named, color-coded clients and filter your workspace accordingly.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4">
              <AccordionTrigger className="text-lg font-bold text-left">What about scheduling cadence?</AccordionTrigger>
              <AccordionContent className="text-zinc-600 leading-relaxed text-base">
                The AI Post Manager creates a smart spread of your content over 30 days, optimizing posting times per platform. You can review, drag-and-drop, or manually edit any scheduled time before approving the campaign.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5">
              <AccordionTrigger className="text-lg font-bold text-left">Is it secure for client credentials?</AccordionTrigger>
              <AccordionContent className="text-zinc-600 leading-relaxed text-base">
                Absolutely. We utilize Enterprise-grade security including JWT auth, TOTP 2FA, Email OTPs, and Cloudflare Turnstile to ensure all connected social accounts remain protected.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-6">
              <AccordionTrigger className="text-lg font-bold text-left">Can I cancel anytime?</AccordionTrigger>
              <AccordionContent className="text-zinc-600 leading-relaxed text-base">
                Yes. All our subscriptions are powered by Stripe and run month-to-month. You can cancel, upgrade, or downgrade your plan at any time right from your billing settings.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="bg-indigo-600 py-24 sm:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:40px_40px] opacity-20"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-white mb-8">
            Stop buying marketing tools.<br/>Start building your marketing department.
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-white text-indigo-600 hover:bg-zinc-100 text-lg h-14 px-8 font-bold" data-testid="final-primary-cta">
              <a href="/pricing">Create Your Workspace</a>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-indigo-400 text-white hover:bg-indigo-500 text-lg h-14 px-8" data-testid="final-secondary-cta">
              <a href="/pricing">Compare Plans</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 py-16 text-zinc-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 bg-indigo-600 rounded flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl tracking-tight text-white">PostToSocial</span>
            </div>
            <p className="max-w-xs leading-relaxed">
              The AI marketing platform that actually publishes for you, across 5 networks (TikTok coming soon).
            </p>
          </div>
          <div>
            <h4 className="text-white font-bold tracking-tight mb-4">Product</h4>
            <ul className="space-y-3">
              <li><a href="#features" className="hover:text-white transition-colors" data-testid="footer-features">Features</a></li>
              <li><a href="/pricing" className="hover:text-white transition-colors" data-testid="footer-pricing">Pricing</a></li>
              <li><a href="/login" className="hover:text-white transition-colors" data-testid="footer-login">Sign in</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-bold tracking-tight mb-4">Legal</h4>
            <ul className="space-y-3">
              <li><a href="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</a></li>
              <li><a href="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms of Service</a></li>
              <li><a href="/data-deletion" className="hover:text-white transition-colors" data-testid="footer-data">Data Deletion</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 pt-8 border-t border-zinc-800 text-sm">
          <p>&copy; {new Date().getFullYear()} PostToSocial. All rights reserved.</p>
        </div>
      </footer>
      <ScrollCtaBanner />
    </div>
  );
};

export default LandingPage;